import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, KeyRound, Mail, Gauge, Smartphone, LogOut, Lock, ShieldCheck, ChevronDown, ChevronUp, Check, Phone, MapPin } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Spinner } from "../components/common";

function maskEmail(email) {
  if (!email || !email.includes("@")) return "your email";
  const [local, domain] = email.split("@");
  return `${local[0]}${"*".repeat(Math.max(3, local.length - 1))}@${domain}`;
}

function SectionLabel({ children }) {
  return <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mt-7 mb-3 px-0.5">{children}</h2>;
}

function StatusBadge({ ok, okLabel, offLabel, testid }) {
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full shrink-0 ${ok ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
      {ok && <Check size={12} strokeWidth={3} />}{ok ? okLabel : offLabel}
    </span>
  );
}

function CardHead({ icon: Icon, title, desc, danger, badge }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div className={`grid place-items-center w-10 h-10 rounded-full shrink-0 ${danger ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}><Icon size={18} /></div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className={`font-display text-base font-semibold leading-tight ${danger ? "text-destructive" : ""}`}>{title}</h3>
          {badge}
        </div>
        {desc && <p className="text-[12px] text-muted-foreground leading-snug mt-0.5">{desc}</p>}
      </div>
    </div>
  );
}

function Switch({ checked, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-primary" : "bg-muted"}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

function PinSection() {
  const [status, setStatus] = useState(null);
  const [pinLength, setPinLength] = useState(4);
  const [form, setForm] = useState({ current_pin: "", pin: "" });
  const [removePin, setRemovePin] = useState("");
  const [showRemove, setShowRemove] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/security/pin");
      setStatus(data);
      if (data.pin_length) setPinLength(data.pin_length);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (form.pin.length !== pinLength) { toast.error(`PIN must be exactly ${pinLength} digits`); return; }
    setBusy(true);
    try {
      await api.post("/security/pin", { pin: form.pin, pin_length: pinLength, current_pin: form.current_pin || null });
      toast.success(status?.pin_set ? "PIN updated" : "PIN set");
      setForm({ current_pin: "", pin: "" });
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const toggle = async (enabled) => {
    try { await api.post("/security/pin/toggle", { enabled }); await load(); toast.success(enabled ? "PIN required for payments" : "PIN turned off"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const doRemove = async () => {
    if (!/^\d{4,6}$/.test(removePin)) { toast.error("Enter your current PIN"); return; }
    setBusy(true);
    try {
      await api.delete("/security/pin", { data: { pin: removePin } });
      toast.success("PIN removed");
      setRemovePin(""); setShowRemove(false);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <div className="rounded-3xl bg-card border border-border p-5" data-testid="pin-section">
      <CardHead icon={Lock} title="Payment PIN" desc="Protect transfers with a 4 or 6-digit PIN."
        badge={<StatusBadge ok={status.pin_set} okLabel="Active" offLabel="Not Set" testid="pin-status-badge" />} />

      {status.pin_set && (
        <div className="flex items-center justify-between py-2 mb-3 border-y border-border">
          <span className="text-sm font-medium">Require PIN for payments</span>
          <Switch checked={status.pin_enabled} onChange={(v) => toggle(v)} testid="pin-enabled-toggle" />
        </div>
      )}

      <form onSubmit={save} className="space-y-3">
        <div>
          <div className="text-xs font-semibold text-muted-foreground mb-2">Choose PIN length</div>
          <div className="grid grid-cols-2 gap-2">
            {[4, 6].map((n) => (
              <button type="button" key={n} data-testid={`pin-length-${n}`}
                onClick={() => { setPinLength(n); setForm((f) => ({ ...f, pin: f.pin.slice(0, n) })); }}
                className={`h-11 rounded-xl text-sm font-semibold border transition-colors ${pinLength === n ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-transparent text-foreground/80"}`}>
                {n} digits
              </button>
            ))}
          </div>
        </div>

        {status.pin_set && (
          <input data-testid="current-pin-input" type="password" inputMode="numeric" maxLength={6} placeholder="Current PIN"
            value={form.current_pin} onChange={(e) => setForm({ ...form, current_pin: e.target.value.replace(/\D/g, "") })}
            className="w-full h-12 px-4 rounded-xl bg-secondary outline-none tracking-[0.5em]" />
        )}
        <input data-testid="new-pin-input" type="password" inputMode="numeric" maxLength={pinLength}
          placeholder={status.pin_set ? `New ${pinLength}-digit PIN` : `Create a ${pinLength}-digit PIN`}
          value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, pinLength) })}
          className="w-full h-12 px-4 rounded-xl bg-secondary outline-none tracking-[0.5em]" />
        <button type="submit" disabled={busy || form.pin.length !== pinLength} data-testid="save-pin-btn"
          className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center disabled:opacity-50">
          {busy ? <Spinner className="w-5 h-5" /> : (status.pin_set ? "Change Payment PIN" : "Set Payment PIN")}
        </button>
      </form>

      {status.pin_set && (
        showRemove ? (
          <div className="flex gap-2 mt-3">
            <input data-testid="remove-pin-input" type="password" inputMode="numeric" maxLength={6} placeholder="Current PIN"
              value={removePin} onChange={(e) => setRemovePin(e.target.value.replace(/\D/g, ""))}
              className="flex-1 h-11 px-4 rounded-xl bg-secondary outline-none tracking-[0.5em]" />
            <button onClick={doRemove} disabled={busy} data-testid="confirm-remove-pin-btn" className="px-4 h-11 rounded-xl bg-destructive/10 text-destructive font-semibold text-sm">Remove</button>
          </div>
        ) : (
          <button onClick={() => setShowRemove(true)} data-testid="remove-pin-btn" className="w-full text-sm text-destructive font-semibold mt-3">Remove PIN</button>
        )
      )}
    </div>
  );
}

function TwoFactorSection({ email }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingEnable, setPendingEnable] = useState(false);
  const [devices, setDevices] = useState([]);

  const loadDevices = async () => {
    try { const { data } = await api.get("/auth/trusted-devices"); setDevices(data); }
    catch { setDevices([]); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
      if (data.enabled) await loadDevices(); else setDevices([]);
    }
    catch (e) { setStatus({ enabled: false, email_hint: "" }); }
  };
  useEffect(() => { load(); }, []);

  const startToggle = (enable) => { setPendingEnable(enable); setOpen(true); setPassword(""); };

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/settings", { enabled: pendingEnable, password });
      toast.success(pendingEnable ? "Two-factor turned on" : "Two-factor turned off");
      setOpen(false); setPassword("");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const revokeDevice = async (id) => {
    try { await api.delete(`/auth/trusted-devices/${id}`); toast.success("Device removed"); await loadDevices(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const revokeAll = async () => {
    try { await api.post("/auth/trusted-devices/revoke-all"); toast.success("All trusted devices removed"); await loadDevices(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!status) return null;

  return (
    <div className="rounded-3xl bg-card border border-border p-5" data-testid="twofa-section">
      <CardHead icon={ShieldCheck} title="Two-Factor Authentication" desc="Add an extra security step when signing in from a new device."
        badge={<StatusBadge ok={!!status.enabled} okLabel="Enabled" offLabel="Disabled" testid="twofa-status-badge" />} />

      <div className="flex items-center gap-2 mb-3 text-sm">
        <Mail size={15} className="text-muted-foreground shrink-0" />
        <span className="font-mono text-foreground/80">{status.email_hint || maskEmail(email)}</span>
      </div>

      <div className="flex items-center justify-between py-2 border-t border-border">
        <span className="text-sm font-medium pr-3">Require email verification for new-device sign-ins</span>
        <Switch checked={!!status.enabled} onChange={(v) => startToggle(v)} testid="twofa-toggle" />
      </div>

      {open && (
        <form onSubmit={confirm} className="mt-3 space-y-3 border-t border-border pt-4">
          <p className="text-xs text-muted-foreground">Confirm your password to {pendingEnable ? "turn on" : "turn off"} two-factor.</p>
          <input data-testid="twofa-password-input" type="password" placeholder="Your password" value={password}
            onChange={(e) => setPassword(e.target.value)} required
            className="w-full h-12 px-4 rounded-xl bg-secondary outline-none" />
          <div className="flex gap-2">
            <button type="button" onClick={() => { setOpen(false); load(); }} className="flex-1 h-11 rounded-xl bg-secondary font-semibold text-sm" data-testid="twofa-cancel-btn">Cancel</button>
            <button type="submit" disabled={busy} data-testid="twofa-confirm-btn" className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-semibold text-sm flex items-center justify-center">
              {busy ? <Spinner className="w-5 h-5" /> : "Confirm"}
            </button>
          </div>
        </form>
      )}

      {status.enabled && devices.length > 0 && (
        <div className="mt-4 border-t border-border pt-4" data-testid="trusted-devices">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold">Trusted devices</span>
            <button onClick={revokeAll} data-testid="revoke-all-trusted-btn" className="text-xs font-semibold text-destructive">Remove all</button>
          </div>
          <p className="text-xs text-muted-foreground mb-2">These devices skip the email code for 30 days.</p>
          <div className="divide-y divide-border">
            {devices.map((d) => (
              <div key={d.device_id} data-testid={`trusted-${d.device_id}`} className="flex items-center gap-3 py-2.5">
                <div className="grid place-items-center w-8 h-8 rounded-full bg-secondary text-primary shrink-0"><Smartphone size={15} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{d.device}{d.current && <span className="ml-2 text-[10px] font-bold text-success bg-success/10 px-2 py-0.5 rounded-full">THIS DEVICE</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{d.ip} · trusted {new Date(d.created_at).toLocaleDateString()}</div>
                </div>
                <button onClick={() => revokeDevice(d.device_id)} data-testid={`revoke-trusted-${d.device_id}`} className="text-xs font-semibold text-destructive">Remove</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DevicesSection() {
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/auth/sessions"); setSessions(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const revokeOthers = async () => {
    setBusy(true);
    try {
      await api.post("/auth/sessions/revoke-others");
      toast.success("Signed out all other devices");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const revokeOne = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/auth/sessions/${id}`);
      toast.success("Device signed out");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const COLLAPSED = 2;
  const visible = sessions ? (expanded ? sessions : sessions.slice(0, COLLAPSED)) : [];
  const hiddenCount = sessions ? Math.max(0, sessions.length - COLLAPSED) : 0;

  return (
    <div className="rounded-3xl bg-card border border-border p-5" data-testid="devices-section">
      <CardHead icon={Smartphone} title="Devices & Sessions" desc="Review where your Jack Cash account is currently signed in." />
      {!sessions ? <Spinner className="w-5 h-5 text-primary" /> : (
        <div className="divide-y divide-border">
          {sessions.length === 0 && <p className="text-sm text-muted-foreground py-2">No active sessions recorded.</p>}
          {visible.map((s) => (
            <div key={s.login_session_id} data-testid={`session-${s.login_session_id}`} className="flex items-center gap-3 py-3">
              <div className="grid place-items-center w-9 h-9 rounded-full bg-secondary text-primary shrink-0"><Smartphone size={16} /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{s.device}{s.current && <span className="ml-2 text-[10px] font-bold text-success bg-success/10 px-2 py-0.5 rounded-full">THIS DEVICE</span>}</div>
                <div className="text-xs text-muted-foreground truncate">{s.ip} · {new Date(s.created_at).toLocaleString()}</div>
              </div>
              {!s.current && (
                <button data-testid={`signout-session-${s.login_session_id}`} onClick={() => revokeOne(s.login_session_id)} disabled={busy}
                  className="shrink-0 text-xs font-semibold text-destructive px-3 py-1.5 rounded-lg bg-destructive/10 hover:bg-destructive/15 transition-colors disabled:opacity-50">Sign Out</button>
              )}
            </div>
          ))}
        </div>
      )}
      {sessions && sessions.length > COLLAPSED && (
        <button data-testid="devices-toggle-btn" onClick={() => setExpanded((v) => !v)}
          className="mt-2 w-full h-10 rounded-xl bg-secondary text-primary font-semibold text-sm flex items-center justify-center gap-1">
          {expanded ? <>Close <ChevronUp size={16} /></> : <>View {hiddenCount} more <ChevronDown size={16} /></>}
        </button>
      )}
      {sessions && sessions.length > 1 && (
        <button data-testid="revoke-others-btn" onClick={revokeOthers} disabled={busy}
          className="mt-3 w-full h-12 rounded-xl bg-destructive/10 text-destructive font-semibold flex items-center justify-center gap-2">
          {busy ? <Spinner className="w-5 h-5" /> : <><LogOut size={16} /> Sign Out All Other Devices</>}
        </button>
      )}
    </div>
  );
}

function LimitsSection() {
  const [limits, setLimits] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/limits"); setLimits(data); setValue(data.monthly_send_limit != null ? (data.monthly_send_limit / 100).toString() : ""); }
    catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/limits", { monthly_send_limit: value === "" ? null : parseFloat(value) });
      toast.success("Spending limit updated");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!limits) return null;
  const pct = limits.monthly_send_limit ? Math.min(100, Math.round((limits.used_this_month / limits.monthly_send_limit) * 100)) : 0;

  return (
    <div className="rounded-3xl bg-card border border-border p-5">
      <CardHead icon={Gauge} title="Monthly spending limit" desc="Cap how much you can send each month. Leave empty for no limit." />
      <div className="flex justify-between text-sm mb-1">
        <span className="text-muted-foreground">Sent this month</span>
        <span className="font-medium">{limits.used_display} / {limits.monthly_send_limit_display}</span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden mb-4">
        <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
          <input data-testid="limit-input" type="number" step="1" min="0" value={value} onChange={(e) => setValue(e.target.value)} placeholder="No limit" className="w-full h-12 pl-7 pr-3 rounded-xl bg-secondary outline-none" />
        </div>
        <button data-testid="limit-save-btn" onClick={save} disabled={busy} className="px-5 h-12 rounded-xl bg-primary text-primary-foreground font-semibold">{busy ? <Spinner className="w-5 h-5" /> : "Save"}</button>
      </div>
    </div>
  );
}

function SecurityScore({ user }) {
  const [pinSet, setPinSet] = useState(false);
  const [twofa, setTwofa] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.allSettled([api.get("/security/pin"), api.get("/auth/2fa/status")]).then(([p, t]) => {
      if (p.status === "fulfilled") setPinSet(!!p.value.data.pin_set);
      if (t.status === "fulfilled") setTwofa(!!t.value.data.enabled);
      setLoaded(true);
    });
  }, []);

  const items = [
    { ok: !!user.email_verified, label: "Verify your email", icon: Mail },
    { ok: pinSet, label: "Set a Payment PIN", icon: Lock },
    { ok: twofa, label: "Turn on two-factor", icon: ShieldCheck },
    { ok: !!user.phone, label: "Add a phone number", icon: Phone },
    { ok: !!(user.address && user.address.line1), label: "Add your home address", icon: MapPin },
  ];
  const score = items.filter((i) => i.ok).length;
  const pct = (score / items.length) * 100;
  const tone = pct >= 80 ? "text-success" : pct >= 40 ? "text-amber-500" : "text-destructive";
  const bar = pct >= 80 ? "bg-success" : pct >= 40 ? "bg-amber-500" : "bg-destructive";
  const label = pct >= 80 ? "Strong" : pct >= 40 ? "Good — keep going" : "Needs attention";

  if (!loaded) return null;

  return (
    <div className="rounded-3xl bg-card border border-border p-5 mb-1" data-testid="security-score">
      <div className="flex items-center gap-3 mb-4">
        <div className={`grid place-items-center w-11 h-11 rounded-full bg-primary/10 ${tone}`}><Gauge size={20} /></div>
        <div className="flex-1">
          <div className="flex items-baseline justify-between">
            <h3 className="font-display text-base font-semibold">Security score</h3>
            <span data-testid="security-score-value" className={`text-sm font-bold ${tone}`}>{score}/{items.length}</span>
          </div>
          <p className={`text-[12px] font-medium ${tone}`}>{label}</p>
        </div>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden mb-4">
        <div className={`h-full rounded-full transition-all duration-500 ${bar}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2.5 text-sm">
            <span className={`grid place-items-center w-5 h-5 rounded-full shrink-0 ${it.ok ? "bg-success text-white" : "bg-muted text-muted-foreground"}`}>
              {it.ok ? <Check size={12} strokeWidth={3} /> : <it.icon size={11} />}
            </span>
            <span className={it.ok ? "text-muted-foreground line-through" : "font-medium"}>{it.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Security() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed. Other sessions signed out.");
      setPw({ current_password: "", new_password: "" });
      await api.post("/auth/refresh").catch(() => {});
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const verifyEmail = async () => {
    try { const { data } = await api.post("/auth/verify-email/request"); toast.message(data.message); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/account");
      setUser(null);
      toast.success("Account deleted");
      nav("/", { replace: true });
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const pwChanged = user.password_changed_at ? new Date(user.password_changed_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : null;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Security & Privacy" subtitle="Protect your account and your money" />

      <SecurityScore user={user} />

      <SectionLabel>Payment PIN</SectionLabel>
      <PinSection />

      <SectionLabel>Spending controls</SectionLabel>
      <LimitsSection />

      <SectionLabel>Two-factor authentication</SectionLabel>
      <TwoFactorSection email={user.email} />

      <SectionLabel>Password</SectionLabel>
      <div className="rounded-3xl bg-card border border-border p-5">
        <CardHead icon={KeyRound} title="Password" desc={pwChanged ? `Last changed: ${pwChanged}` : "Keep your account secure with a strong password."} />
        <div className="flex items-start gap-2 rounded-xl bg-secondary/60 px-3 py-2.5 mb-3">
          <LogOut size={14} className="text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-[12px] text-muted-foreground leading-snug">Changing your password will sign you out of all your other devices.</p>
        </div>
        <form onSubmit={changePw} className="space-y-3">
          <input data-testid="current-password-input" type="password" placeholder="Current password" value={pw.current_password}
            onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
            className="w-full h-12 px-4 rounded-xl bg-secondary outline-none" required />
          <input data-testid="new-password-input" type="password" placeholder="New password (min 8)" value={pw.new_password}
            onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
            className="w-full h-12 px-4 rounded-xl bg-secondary outline-none" required />
          <button type="submit" disabled={busy} data-testid="change-password-btn"
            className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center">
            {busy ? <Spinner className="w-5 h-5" /> : "Change Password"}
          </button>
        </form>
      </div>

      <SectionLabel>Email verification</SectionLabel>
      <div className="rounded-3xl bg-card border border-border p-5" data-testid="email-section">
        <CardHead icon={Mail} title="Email Address" desc={<span className="font-mono">{maskEmail(user.email)}</span>}
          badge={<StatusBadge ok={!!user.email_verified} okLabel="Verified" offLabel="Not Verified" testid="email-status-badge" />} />
        {!user.email_verified && (
          <button data-testid="verify-email-btn" onClick={verifyEmail}
            className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold">Verify Email</button>
        )}
      </div>

      <SectionLabel>Devices & sessions</SectionLabel>
      <DevicesSection />

      <SectionLabel>Danger zone</SectionLabel>
      <div className="rounded-3xl border border-destructive/20 bg-destructive/5 p-5 mt-1">
        <CardHead icon={Trash2} title="Delete Account" desc="Permanently delete your Jack Cash account and associated data." danger />
        <div className="flex items-start gap-2 rounded-xl bg-destructive/10 px-3 py-2.5 mb-4">
          <p className="text-[12px] text-destructive leading-snug font-medium">You must withdraw your remaining balance before deleting your account.</p>
        </div>
        {!delConfirm ? (
          <button data-testid="delete-account-btn" onClick={() => setDelConfirm(true)} className="w-full h-12 rounded-xl bg-destructive/10 text-destructive font-semibold">Delete Account</button>
        ) : (
          <div className="flex gap-3">
            <button data-testid="cancel-delete-btn" onClick={() => setDelConfirm(false)} className="flex-1 h-12 rounded-xl bg-secondary font-semibold">Cancel</button>
            <button data-testid="confirm-delete-btn" onClick={deleteAccount} disabled={busy} className="flex-1 h-12 rounded-xl bg-destructive text-white font-semibold flex items-center justify-center">{busy ? <Spinner className="w-5 h-5" /> : "Confirm Delete"}</button>
          </div>
        )}
      </div>
    </div>
  );
}
